// Must stay the first import: it pins Firebase's defaults before the SDK can
// read them from a cookie. See src/firebase-defaults.js.
import './firebase-defaults.js';
import { initializeApp, getApp } from 'firebase/app';
import { getAuth, OAuthProvider, signInWithPopup, signInWithCustomToken } from 'firebase/auth';
import * as ciap from 'gcip-iap';

import { accessHelpMailto } from './access-help.js';

// IAP supplies its protocol parameters in the initial URL. The browser must
// receive them, but they are public client configuration rather than a secret.
// Capture them synchronously and immediately remove them from the address bar;
// gcip-iap receives the original URL only while it is being constructed.
const initialIapUrl = new URL(window.location.href);
const isIapRequest = initialIapUrl.searchParams.has('apiKey') && initialIapUrl.searchParams.has('mode');
const iapLocation = `${initialIapUrl.pathname}${initialIapUrl.search}${initialIapUrl.hash}`;
const cleanIapUrl = new URL(initialIapUrl);
if (isIapRequest) {
  for (const parameter of ['apiKey', 'mode', 'tid', 'state', 'redirect_uri', 'hl']) {
    cleanIapUrl.searchParams.delete(parameter);
  }
  window.history.replaceState(window.history.state, document.title, `${cleanIapUrl.pathname}${cleanIapUrl.search}${cleanIapUrl.hash}`);
}

const config = await fetch('/api/config', { credentials: 'same-origin' }).then(response => response.json());
const status = document.querySelector('#status');
const statusAction = document.querySelector('#status-action');
const ssoButton = document.querySelector('#sso-button');
const fallbackButton = document.querySelector('#fallback-button');
const chooseStep = document.querySelector('#choose-step');
const emailForm = document.querySelector('#email-form');
const otpForm = document.querySelector('#otp-form');
const emailInput = document.querySelector('#email');
const codeInput = document.querySelector('#code');
const gate = document.querySelector('.gate');
const controls = [...document.querySelectorAll('button, input')];
let activeAuth;
let completeSignIn;
let failSignIn;
let challengeId;
let email;
let busyAction;
let savedControlStates = [];
let iapProgressVisible = false;
let preAuthorizedUserId;
let returningToProtectedApp = false;

// EVERY SENTENCE A PERSON CAN SEE ON THIS PAGE, in the client's own words
// (their review of 2026-10-07). Nothing else reaches the status line: a raw
// error such as "auth/popup-closed-by-user" is classified by `errorMessage`
// below and never printed.
const HELP_EMAIL = 'help_intranet@hunterdouglas.com';
const MESSAGES = {
  notInDirectory: `We couldn't find you in the employee directory, so we can't sign you in just yet. Please email ${HELP_EMAIL} and we'll sort it out.`,
  ourSide: `Something isn't working on our side. Please try again in a moment. If it keeps happening, email ${HELP_EMAIL}.`,
  popupClosed: "The Microsoft sign-in window closed before we could finish. Please try again. If your browser blocked the window, allow pop-ups for this site.",
  unknown: `Something isn't working and we're not sure what. Please try again, or email ${HELP_EMAIL} if it keeps happening.`,
  rateLimited: `You've requested several codes in a short time. Please wait an hour and try again, or email ${HELP_EMAIL} if you need to get in sooner.`,
  codeLocked: "You've tried that code too many times. Please request a new one.",
  // Not yet in the client's 2026-10-07 review: added with the gateway's
  // mailbox send-rate guard and per-network sign-in limits.
  busy: "We're sending a lot of codes right now. Please wait a minute and try again.",
  outOfDate: 'This page is out of date. Please go back to the intranet and sign in from there.',
  signedOut: "You're signed out. See you next time."
};

function waitPhrase(retryAfterSeconds) {
  const minutes = Math.max(1, Math.ceil((Number(retryAfterSeconds) || 60) / 60));
  if (minutes > 90) return `${Math.ceil(minutes / 60)} hours`;
  return minutes === 1 ? 'a minute' : `${minutes} minutes`;
}

function slowDownMessage(retryAfterSeconds) {
  return `There have been too many sign-in attempts from your network. Please wait ${waitPhrase(retryAfterSeconds)} and try again, or email ${HELP_EMAIL} if you need to get in sooner.`;
}

// Not yet in the client's 2026-10-07 review: added with the gateway's daily
// limit on wrong codes for one address (cybersecurity review, AUTH-3).
function accountLockedMessage(retryAfterSeconds) {
  return `There have been too many incorrect codes for this address. Please wait ${waitPhrase(retryAfterSeconds)} and try again, or email ${HELP_EMAIL} if you need to get in sooner.`;
}

// A 429 from the gateway carries a reason and a Retry-After. "busy" is the
// shared mailbox being at its sending rate, which clears within a minute;
// anything else is a limit on this person or network.
async function rateLimitError(response, fallbackCode) {
  const body = await response.clone().json().catch(() => ({}));
  if (body.reason === 'busy') return codedError('intranet/busy');
  const error = codedError(body.reason === 'account-locked' ? 'intranet/account-locked' : fallbackCode);
  error.retryAfterSeconds = response.headers.get('retry-after');
  return error;
}

function codeInvalidMessage() {
  return `That code didn't work. Codes expire after ${otpLifetime() ?? '5 minutes'}, so please request a new one.`;
}

// Firebase's codes for a popup the person closed, or the browser refused to open.
const POPUP_CLOSED_CODES = new Set([
  'auth/popup-closed-by-user', 'auth/cancelled-popup-request', 'auth/popup-blocked', 'auth/user-cancelled'
]);

function codedError(code) {
  const error = new Error(code);
  error.code = code;
  return error;
}

function accessDeniedError() {
  return codedError('intranet/access-denied');
}

function isAccessDeniedError(error) {
  return error?.code === 'intranet/access-denied';
}

/** The one sentence a person sees for an error, whatever threw it. */
function errorMessage(error) {
  const code = error?.code;
  if (code === 'intranet/access-denied') return MESSAGES.notInDirectory;
  if (code === 'intranet/unavailable') return MESSAGES.ourSide;
  if (code === 'intranet/rate-limited') return MESSAGES.rateLimited;
  if (code === 'intranet/busy') return MESSAGES.busy;
  if (code === 'intranet/slow-down') return slowDownMessage(error.retryAfterSeconds);
  if (code === 'intranet/account-locked') return accountLockedMessage(error.retryAfterSeconds);
  if (code === 'intranet/code-locked') return MESSAGES.codeLocked;
  if (code === 'intranet/code-invalid') return codeInvalidMessage();
  if (POPUP_CLOSED_CODES.has(code)) return MESSAGES.popupClosed;
  return MESSAGES.unknown;
}

function trace(stage) {
  return fetch('/api/auth/diagnostics', {
    method: 'POST', headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ stage }), keepalive: true
  }).catch(() => undefined);
}

async function getCaptchaToken(action) {
  if (!config.recaptchaSiteKey) return null;
  if (!window.grecaptcha?.enterprise) {
    await new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(config.recaptchaSiteKey)}`;
      script.onload = resolve;
      script.onerror = () => reject(new Error('Unable to load bot protection.'));
      document.head.appendChild(script);
    });
  }
  await new Promise(resolve => window.grecaptcha.enterprise.ready(resolve));
  return window.grecaptcha.enterprise.execute(config.recaptchaSiteKey, { action });
}

// The help address is a link wherever a message names it. Built from text
// nodes, never innerHTML, so nothing in a message can become markup.
function message(text, isError = false, action = null) {
  status.replaceChildren();
  const parts = text.split(HELP_EMAIL);
  parts.forEach((part, index) => {
    if (part) status.append(document.createTextNode(part));
    if (index < parts.length - 1) {
      const link = document.createElement('a');
      link.href = `mailto:${HELP_EMAIL}`;
      link.textContent = HELP_EMAIL;
      status.append(link);
    }
  });
  status.dataset.error = String(isError);
  if (action?.href) {
    statusAction.href = action.href;
    statusAction.textContent = action.label;
    statusAction.hidden = false;
  } else {
    statusAction.hidden = true;
    statusAction.removeAttribute('href');
  }
}

// The gate shows one step at a time: choose a way in, enter a work email, or
// enter the code. Each step names itself, so a step change clears the status.
function showStep(step) {
  chooseStep.hidden = step !== 'choose';
  emailForm.hidden = step !== 'email';
  otpForm.hidden = step !== 'code';
  message('');
  const field = { choose: ssoButton, email: emailInput, code: codeInput }[step];
  field?.focus();
}

// The prototype's inline check, shown under the field instead of the
// browser's own bubble. The server still validates everything it receives.
function checkField(input, valid) {
  const error = document.getElementById(input.getAttribute('aria-describedby'));
  if (valid) input.removeAttribute('aria-invalid');
  else input.setAttribute('aria-invalid', 'true');
  error.hidden = valid;
  if (!valid) input.focus();
  return valid;
}

function otpLifetime() {
  const minutes = Math.round(Number(config.otpTtlSeconds) / 60);
  if (!Number.isFinite(minutes) || minutes < 1) return null;
  return minutes === 1 ? '1 minute' : `${minutes} minutes`;
}

function iapReturnOrigins() {
  return Array.isArray(config.iapReturnOrigins)
    ? config.iapReturnOrigins.filter(origin => typeof origin === 'string')
    : [];
}

function authDomainForCurrentHost() {
  const configuredDomains = Array.isArray(config.authDomains)
    ? config.authDomains.filter(domain => typeof domain === 'string')
    : [];
  const currentHost = window.location.host.toLowerCase();
  // A Firebase Hosting rewrite to Cloud Run does not need to preserve the
  // browser Host header: only the browser knows which Hosting domain served
  // the page. Use it only when it is explicitly allowlisted by the gateway.
  return configuredDomains.includes(currentHost) ? currentHost : config.authDomain;
}

function protectedAppUrl() {
  const origin = iapReturnOrigins()[0];
  return origin ? new URL('/intranet', origin).toString() : null;
}

function retireIapHandoff() {
  completeSignIn = undefined;
  failSignIn = undefined;
  activeAuth = undefined;
  preAuthorizedUserId = undefined;
}

function returnToProtectedApp() {
  if (returningToProtectedApp) return;
  returningToProtectedApp = true;
  retireIapHandoff();
  iapProgressVisible = false;
  finishBusy();
  chooseStep.hidden = false;
  document.querySelector('#choices').hidden = true;
  emailForm.hidden = true;
  otpForm.hidden = true;
  controls.forEach(control => { control.disabled = true; });
  void trace('iap-stale-page-return');

  // ONE MESSAGE FOR THIS SITUATION, with a button rather than an instruction
  // (two different sentences used to appear here depending on the path). The page still returns on its own when it
  // knows where to go; the button is there if that is slow or blocked.
  const returnUrl = protectedAppUrl();
  message(MESSAGES.outOfDate, false, returnUrl ? { label: 'Go to the intranet', href: returnUrl } : null);
  if (returnUrl) window.location.replace(returnUrl);
}

function hasLiveIapHandoff() {
  return isIapRequest && !returningToProtectedApp && Boolean(activeAuth) && typeof completeSignIn === 'function';
}

function requireLiveIapHandoff() {
  if (hasLiveIapHandoff()) return true;
  returnToProtectedApp();
  return false;
}

function completeIapHandoff(credential) {
  if (!hasLiveIapHandoff()) {
    returnToProtectedApp();
    return false;
  }
  const resolve = completeSignIn;
  // A gcip-iap continuation is single-use. Clearing it immediately prevents
  // a browser-restored page from attempting to complete the old transaction.
  completeSignIn = undefined;
  failSignIn = undefined;
  resolve(credential);
  return true;
}

function updateBusySurface() {
  const busy = Boolean(busyAction || iapProgressVisible);
  document.body.dataset.busy = String(busy);
  gate?.setAttribute('aria-busy', String(busy));
}

function setButtonLabel(button, label) {
  if (!button.dataset.idleLabel) button.dataset.idleLabel = button.textContent.trim();
  button.textContent = label;
}

function beginBusy(button, label) {
  // CSS alone cannot stop keyboard submits or a duplicate event. This lock is
  // the final guard while the UI is visually disabled.
  if (busyAction) return false;
  busyAction = button;
  savedControlStates = controls.map(control => [control, control.disabled]);
  controls.forEach(control => { control.disabled = true; });
  button.dataset.loading = 'true';
  button.setAttribute('aria-busy', 'true');
  setButtonLabel(button, label);
  updateBusySurface();
  return true;
}

function updateBusyLabel(label) {
  if (busyAction) setButtonLabel(busyAction, label);
}

function finishBusy() {
  if (!busyAction) return;
  savedControlStates.forEach(([control, disabled]) => { control.disabled = disabled; });
  savedControlStates = [];
  const button = busyAction;
  busyAction = undefined;
  delete button.dataset.loading;
  button.removeAttribute('aria-busy');
  button.textContent = button.dataset.idleLabel ?? button.textContent;
  updateBusySurface();
}

async function authorizeIntranetAccess(user) {
  await trace('sso-process-user-started');
  const idToken = await user.getIdToken(true);
  await trace('sso-atlas-request-started');
  // A request that never reaches the gateway is our side failing, not the
  // person's account: it reads as the "try again in a moment" message.
  const response = await fetch('/api/auth/sso/authorize', {
    method: 'POST',
    headers: { authorization: `Bearer ${idToken}` }
  }).catch(() => { throw codedError('intranet/unavailable'); });
  await trace('sso-atlas-request-finished');
  if (response.ok) return;

  // The gateway intentionally returns the same safe result for every denied
  // account. Do not expose which Atlas or identity check rejected the user.
  if (response.status === 401 || response.status === 403) throw accessDeniedError();
  if (response.status === 429) throw await rateLimitError(response, 'intranet/slow-down');
  throw codedError('intranet/unavailable');
}

async function signOutDeniedUser() {
  // Keep a rejected account from being restored by Firebase on a later page
  // load. This only clears this authentication-page session, not Entra's SSO
  // browser session, so the user can select a different corporate account.
  if (activeAuth) await activeAuth.signOut().catch(() => undefined);
}

class Handler {
  getAuth(apiKey, tenantId) {
    if (!apiKey) throw new Error('Identity Platform API key is missing.');
    const name = tenantId || '[DEFAULT]';
    let app;
    try { app = getApp(name); } catch {
      app = initializeApp({ apiKey, authDomain: authDomainForCurrentHost(), projectId: config.projectId }, name);
    }
    activeAuth = getAuth(app);
    activeAuth.tenantId = tenantId || null;
    return activeAuth;
  }

  startSignIn(auth) {
    activeAuth = auth;
    return new Promise((resolve, reject) => {
      completeSignIn = resolve;
      failSignIn = reject;
      document.querySelector('#choices').hidden = false;
      ssoButton.focus();
    });
  }

  async processUser(user) {
    updateBusyLabel('Checking your access…');
    try {
      const userId = typeof user?.uid === 'string' ? user.uid : null;
      if (userId && preAuthorizedUserId === userId) {
        // The popup path already completed the server-side authorization
        // before resolving gcip-iap's sign-in promise. Consume the marker so
        // restored sessions are always checked again.
        preAuthorizedUserId = undefined;
      } else {
        await authorizeIntranetAccess(user);
      }
      updateBusyLabel('Signing you in…');
      return user;
    } catch (error) {
      preAuthorizedUserId = undefined;
      if (isAccessDeniedError(error)) await signOutDeniedUser();
      finishBusy();
      throw error;
    }
  }

  completeSignOut() {
    preAuthorizedUserId = undefined;
    finishBusy();
    // Not an error, so not styled as one, and the way back in is a button.
    const returnUrl = protectedAppUrl();
    message(MESSAGES.signedOut, false, returnUrl ? { label: 'Sign in again', href: returnUrl } : null);
    return Promise.resolve();
  }
  showProgressBar() { iapProgressVisible = true; updateBusySurface(); }
  hideProgressBar() { iapProgressVisible = false; updateBusySurface(); }
  handleError(error) { finishBusy(); message(errorMessage(error), true); }
}

ssoButton.addEventListener('click', async () => {
  if (!requireLiveIapHandoff()) return;
  if (!beginBusy(ssoButton, 'Opening Microsoft sign-in…')) return;
  try {
    // Do not await telemetry here: the popup must be opened in the original
    // user-gesture task or browsers will reject it.
    trace('sso-clicked');
    message('Opening Microsoft sign-in…');
    const provider = new OAuthProvider(config.entraProviderId);
    // Microsoft Entra returns the `email` claim only when the client asks for
    // it (or when an administrator configures it as an optional ID-token claim).
    // Atlas authorization intentionally uses only that Entra identity claim.
    provider.addScope('email');
    provider.setCustomParameters({ prompt: 'select_account' });
    const credential = await signInWithPopup(activeAuth, provider);
    await trace('sso-popup-succeeded');
    if (!requireLiveIapHandoff()) return;
    updateBusyLabel('Checking your access…');

    // Pre-authorize before resolving the gcip-iap promise. A rejected account
    // must stay on this page; resolving first would let gcip-iap begin its IAP
    // redirect exchange before the Atlas decision is known.
    await authorizeIntranetAccess(credential.user);
    preAuthorizedUserId = credential.user.uid;
    if (!completeIapHandoff(credential)) preAuthorizedUserId = undefined;
  } catch (error) {
    const code = String(error?.code || 'unknown').replace(/[^a-z0-9-]/gi, '-').toLowerCase();
    preAuthorizedUserId = undefined;
    if (isAccessDeniedError(error)) await signOutDeniedUser();
    finishBusy();
    void trace(`sso-popup-failed-${code}`);
    message(errorMessage(error), true);
  }
});

fallbackButton.addEventListener('click', () => {
  if (!requireLiveIapHandoff()) return;
  if (!config.fallbackEnabled) return message('Email fallback is not enabled yet.', true);
  showStep('email');
});

document.querySelectorAll('.back').forEach(button => {
  button.addEventListener('click', () => showStep(button.dataset.step));
});

emailInput.addEventListener('input', () => checkField(emailInput, true));
codeInput.addEventListener('input', () => checkField(codeInput, true));

// "Having access issues?" (2026-10-08): each problem's draft to the help mailbox
// is built at the moment of the click, so it names the step the person is on,
// the email they typed and the error the page is showing them. Each link's own
// href in index.html is the draft that opens if this script never ran.
const accessHelp = document.querySelector('#access-help');
accessHelp?.addEventListener('click', event => {
  const link = event.target instanceof Element ? event.target.closest('a[data-access-tag]') : null;
  if (!link) return;
  const step = !otpForm.hidden ? 'code' : !emailForm.hidden ? 'email' : 'choose';
  link.href = accessHelpMailto({
    tag: link.dataset.accessTag,
    step,
    email: emailInput.value,
    message: status.dataset.error === 'true' ? status.textContent : '',
    href: window.location.href,
    now: new Date()
  });
  accessHelp.open = false;
});

emailForm.addEventListener('submit', async event => {
  event.preventDefault();
  if (!requireLiveIapHandoff()) return;
  // Disabled form controls are intentionally omitted by FormData. Capture the
  // submitted value before beginBusy locks the interface.
  const submittedEmail = new FormData(emailForm).get('email');
  email = typeof submittedEmail === 'string' ? submittedEmail.trim().toLowerCase() : '';
  if (!checkField(emailInput, /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email))) return;
  const submitButton = emailForm.querySelector('button[type="submit"]');
  if (!beginBusy(submitButton, 'Sending verification code…')) return;
  try {
    const captchaToken = await getCaptchaToken('otp_request');
    const response = await fetch('/api/auth/otp/request', {
      method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email, captchaToken })
    });
    // EVERY ADDRESS REACHES THE CODE SCREEN (the client's #6): the server answers
    // a known and an unknown address the same way, and the code screen carries
    // the help address for an employee whose record is missing.
    if (response.status === 429) throw await rateLimitError(response, 'intranet/rate-limited');
    if (response.status >= 500) throw codedError('intranet/unavailable');
    if (!response.ok) throw codedError('intranet/unknown');
    const body = await response.json();
    challengeId = body.challengeId;
    // The step's own line carries the server's generic promise: a code goes
    // out only if the address can sign in, and it is never confirmed here.
    document.querySelector('#sent-email').textContent = email;
    const lifetime = otpLifetime();
    if (lifetime) document.querySelector('#otp-lifetime').textContent = lifetime;
    codeInput.value = '';
    checkField(codeInput, true);
    showStep('code');
  } catch (error) {
    message(errorMessage(error), true);
  } finally {
    finishBusy();
  }
});

otpForm.addEventListener('submit', async event => {
  event.preventDefault();
  if (!requireLiveIapHandoff()) return;
  // See the email form above: retain the one-time code before disabling it.
  const submittedCode = new FormData(otpForm).get('code');
  const code = typeof submittedCode === 'string' ? submittedCode.replace(/\D/g, '') : '';
  if (!checkField(codeInput, code.length === 6)) return;
  const submitButton = otpForm.querySelector('button[type="submit"]');
  if (!beginBusy(submitButton, 'Verifying code…')) return;
  let handedOffToIap = false;
  try {
    const response = await fetch('/api/auth/otp/verify', {
      method: 'POST', headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, code, challengeId })
    });
    if (response.status === 429) throw await rateLimitError(response, 'intranet/slow-down');
    const body = await response.json().catch(() => ({}));
    if (response.status === 401) {
      throw codedError(body.reason === 'locked' ? 'intranet/code-locked' : 'intranet/code-invalid');
    }
    if (response.status >= 500) throw codedError('intranet/unavailable');
    if (!response.ok) throw codedError('intranet/unknown');
    if (!requireLiveIapHandoff()) return;
    updateBusyLabel('Signing you in…');
    handedOffToIap = completeIapHandoff(await signInWithCustomToken(activeAuth, body.customToken));
  } catch (error) {
    message(errorMessage(error), true);
  } finally {
    // A successful custom-token sign-in hands control to IAP. Keep the page
    // locked until IAP redirects; all failures return the controls to the user.
    if (!handedOffToIap) finishBusy();
  }
});

window.addEventListener('unhandledrejection', event => {
  if (!isIapRequest || returningToProtectedApp) {
    returnToProtectedApp();
    return;
  }
  preAuthorizedUserId = undefined;
  finishBusy();
  failSignIn?.(event.reason);
  message(errorMessage(event.reason), true);
});

const handler = new Handler();

window.addEventListener('pagehide', retireIapHandoff);
window.addEventListener('pageshow', event => {
  // The browser's Back button can restore this page from BFCache after IAP
  // has already consumed its one-time state. Never revive that old UI.
  if (event.persisted) returnToProtectedApp();
});

if (!isIapRequest) {
  // This page is only a custom IAP UI. A URL without IAP protocol parameters
  // is a direct/history navigation, not a usable login transaction.
  returnToProtectedApp();
} else {
  try {
    let iap;
    window.history.replaceState(window.history.state, document.title, iapLocation);
    try {
      iap = new ciap.Authentication(handler);
    } finally {
      window.history.replaceState(window.history.state, document.title, `${cleanIapUrl.pathname}${cleanIapUrl.search}${cleanIapUrl.hash}`);
    }
    // Do not persist IAP's original URL for browser-history recovery. The
    // continuation below retains it for the active protocol exchange, while
    // a stale page must return only to the canonical intranet route.
    void iap.start().catch(error => {
      finishBusy();
      message(errorMessage(error), true);
      return trace('iap-start-failed');
    });
    trace('iap-ui-ready');
  } catch (error) {
    message(errorMessage(error), true);
  }
}
